import { CSSProperties, FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";

import { translationKeys } from "../../../models/app-translation-keys";
import { UploadFile } from "../../../models/classes/upload-file.class";
import { IFileUploadErrors } from "../../../models/interfaces/file-upload-errors.interface";
import { IProgressedFileResult } from "../../../models/interfaces/progressed-file-result.interface";
import { FileListComponent, SimpleFileListComponent } from "./FileLists";
import { FileUploadArea, IFileType } from "./FileUploadArea/FileUploadArea";
import { FileUploadErrorsComponent } from "./FileUploadErrors/FileUploadErrors";

import "./FileUpload.scss";

interface IFileUploadProps {
  fileSizeLimitMB: number;
  existingFilesNames?: string[];
  smallUploadArea?: boolean;
  smallFileList?: boolean;
  files?: UploadFile[];
  onChange?: (uploadFiles: UploadFile[]) => void;
  disabled?: boolean;
  showAllowedFileTypes?: boolean;
  allowedFileTypes?: IFileType[];
  isSingleFileUpload?: boolean;
  maxConcurrentProcessedFiles?: number;
  showRemoveAllButton?: boolean;
  progressingFileIds?: string[];
  fileUploadAreaStyles?: CSSProperties;
  uploadTemplate?: (
    uploadFile: UploadFile,
    setIsFileProcessing: (id: string, isProcessing: boolean) => void,
    downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void,
  ) => ReactNode;
  fileListTemplate?: (uploadFile: UploadFile[], handleRemoveFile: (file: UploadFile) => void) => ReactNode;
  uploadOnly?: boolean;
}

export const FileUploadComponent: FunctionComponent<IFileUploadProps> = (props: IFileUploadProps) => {
  const { t } = useTranslation();

  const [errors, setErrors] = useState<IFileUploadErrors>({});

  // props.files (in the parent) may be be undefined so we need this state locally
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);

  useEffect(() => {
    setUploadFiles(props.files ? Array.from(props.files) : []);
  }, [props.files]);

  const onFilesSelected = (files: UploadFile[]) => {
    if (props.isSingleFileUpload) {
      // Only allow one file to be uploaded
      const newFiles = files.slice(0, 1);
      setFiles(newFiles);
      return;
    }

    const currentFilesCount = uploadFiles.length;
    const filesCount = files.length;

    if (props.maxConcurrentProcessedFiles) {
      if (currentFilesCount + filesCount > props.maxConcurrentProcessedFiles) {
        files = files.slice(0, props.maxConcurrentProcessedFiles - currentFilesCount);
        setErrors(prevs => ({ ...prevs, hasReachMaximalFiles: true }));
      } else {
        setErrors(prevs => ({ ...prevs, hasReachMaximalFiles: false }));
      }
    }

    // Add new files to the beginning of the list
    const newFiles = [...files, ...uploadFiles];
    setFiles(newFiles);
  };

  const setFiles = (files: UploadFile[]) => {
    const validatedFiles = files?.filter(f => !f?.errors?.length) ?? [];
    setUploadFiles(validatedFiles);
    props.onChange?.(validatedFiles);
  };

  const handleRemoveFile = (filetoDelete: UploadFile) => {
    const newFiles = uploadFiles.filter(uploadFile => uploadFile.file !== filetoDelete.file);
    setFiles(newFiles);
    setErrors(prevs => ({ ...prevs, hasReachMaximalFiles: false }));
  };

  const handleRemoveAllFiles = () => {
    setFiles([]);
    resetErrors();
  };

  const resetErrors = () => {
    setErrors({});
  };

  const fileListContentMemo = useMemo(
    () => (
      <div className="file-upload-list">
        {!props.isSingleFileUpload && props.showRemoveAllButton && !!uploadFiles?.length && (
          <div className="file-list-controls">
            <Button label={t(translationKeys.upload.removeAllFiles)} onClick={handleRemoveAllFiles} />
          </div>
        )}

        {!!props.fileListTemplate && props.fileListTemplate(uploadFiles, handleRemoveFile)}

        {!props.fileListTemplate &&
          (props.uploadTemplate ? (
            <FileListComponent
              uploadFiles={uploadFiles}
              handleRemoveFile={handleRemoveFile}
              uploadTemplate={props.uploadTemplate}
            />
          ) : (
            <SimpleFileListComponent
              uploadFiles={uploadFiles}
              handleRemoveFile={handleRemoveFile}
              smallFileList={props.smallFileList}
              progressingFileIds={props.progressingFileIds}
            />
          ))}
      </div>
    ),
    [uploadFiles, props.smallFileList],
  );

  return (
    <div className="file-upload-container">
      <FileUploadArea
        disabled={props.disabled}
        uploadFiles={uploadFiles}
        showAllowedFileTypes={props.showAllowedFileTypes}
        existingFilesNames={props.existingFilesNames}
        allowedFileTypes={props.allowedFileTypes}
        onFilesSelected={onFilesSelected}
        fileSizeLimitMB={props.fileSizeLimitMB}
        isSingleFileUpload={props.isSingleFileUpload}
        maxConcurrentProcessedFiles={props.maxConcurrentProcessedFiles}
        smallUploadArea={props.smallUploadArea}
        styles={props.fileUploadAreaStyles}
      />
      {!!Object.keys(errors).length && <FileUploadErrorsComponent errors={errors} />}

      {!props.uploadOnly && !!uploadFiles?.length && fileListContentMemo}
    </div>
  );
};
